import React, { useContext, useEffect, useState } from 'react';
import { Alert, Box, Paper, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from 'dayjs';

import ApiService from '../../common/services/ApiService';
import AuthContext from '../../auth/context/AuthContext';

const BASE_BAR_COLOR = '#3498db';
const CURRENT_MONTH_COLOR = '#1abc9c';
const GRID_COLOR = '#e6ecf3';

function renderMonthTick({ x, y, payload }) {
  const label = String(payload.value || '');
  const [month = '', year = ''] = label.split(' ');

  return (
    <g transform={`translate(${x}, ${y + 12})`}>
      <text
        x={0}
        y={0}
        textAnchor="middle"
        fill="#1f2d4d"
        fontSize={12}
        fontWeight={600}
      >
        <tspan x={0} dy={0}>{month}</tspan>
        <tspan x={0} dy={14}>{year}</tspan>
      </text>
    </g>
  );
}

function MonthlyTooltip({ active, payload, label, tooltipLabel }) {
  if (!active || !payload?.length) {
    return null;
  }

  const value = Number(payload[0]?.value ?? 0);
  const barColor = payload[0]?.payload?.isCurrentMonth ? CURRENT_MONTH_COLOR : BASE_BAR_COLOR;

  return (
    <Paper
      elevation={3}
      sx={{
        minWidth: 180,
        border: `2px solid ${barColor}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box p={1}>
        <Typography variant="subtitle2" sx={{ color: 'text.primary', textAlign: 'left' }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {tooltipLabel}
        </Typography>
        <Typography variant="h6" color="text.primary">
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function MonthlyReferralsOverTimeChart({
  title,
  subtitle,
  tooltipLabel,
  loadingLabel = 'Loading monthly referrals...',
  noDataLabel = 'No data found',
  noteLabel = 'Current month bars are highlighted in green.',
  endpointDirection,
  responseKey,
  startDate,
  endDate,
}) {
  const currentUser = useContext(AuthContext);
  const clinicId = currentUser?.clinic?.id || currentUser?.clinics?.[0]?.id || null;
  const currentMonthKey = dayjs().format('YYYY-MM');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const rangeStart = startDate ? dayjs(startDate) : dayjs().startOf('month').subtract(11, 'month');
  const rangeEnd = endDate ? dayjs(endDate) : dayjs().endOf('month');
  const rangeStartValue = rangeStart.format('YYYY-MM-DD');
  const rangeEndValue = rangeEnd.format('YYYY-MM-DD');

  useEffect(() => {
    if (!clinicId) {
      setChartData([]);
      setLoading(false);
      setError('');
      return;
    }

    let isMounted = true;

    const loadMonthlyReferrals = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await ApiService.post(
          ApiService.apiUrl('integrations/clinics/referral-counts'),
          {
            clinic_id: clinicId,
            date_from: rangeStartValue,
            date_to: rangeEndValue,
            group_by: 'month',
            direction: endpointDirection,
          }
        );

        if (!isMounted) {
          return;
        }

        const monthlyReferrals = response?.data?.monthly_referrals
          || (responseKey ? response?.data?.[responseKey] : null)
          || [];

        setChartData(monthlyReferrals.map((row) => ({
          ...row,
          referrals: Number(row?.referrals ?? 0),
          isCurrentMonth: row?.month_key === currentMonthKey,
        })));
      } catch (requestError) {
        if (!isMounted) {
          return;
        }

        const message = requestError?.data?.message
          || requestError?.data?.error
          || `Unable to load ${title.toLowerCase()}.`;
        setError(message);
        setChartData([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadMonthlyReferrals();

    return () => {
      isMounted = false;
    };
  }, [clinicId, currentMonthKey, endpointDirection, rangeEndValue, rangeStartValue, responseKey, title]);

  const totalReferrals = chartData.reduce((sum, row) => sum + Number(row.referrals ?? 0), 0);

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.15 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {subtitle}
            </Typography>
          </Box>

          <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                bgcolor: 'success.light',
                border: '1px solid',
                borderColor: 'success.main',
                color: 'success.dark',
                fontWeight: 700,
                mb: 0.75,
              }}
            >
              12-Month Total: {totalReferrals}
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {noteLabel}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
            {error}
          </Alert>
        )}

        {loading && !chartData.length ? (
          <Typography variant="body2" sx={{ py: 4, color: 'text.secondary' }}>
            {loadingLabel}
          </Typography>
        ) : null}

        {!loading && !error && !chartData.length ? (
          <Typography variant="body2" sx={{ py: 4, color: 'text.secondary' }}>
            {noDataLabel}
          </Typography>
        ) : null}

        {!!chartData.length && !error && (
          <Box sx={{ mt: 2, height: 460, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 48, right: 18, left: 18, bottom: 18 }}
                barCategoryGap="12%"
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={GRID_COLOR} />
                <XAxis
                  dataKey="label"
                  tick={renderMonthTick}
                  interval={0}
                  height={50}
                  tickLine={false}
                  axisLine={{ stroke: '#d6dde6' }}
                />
                <YAxis
                  allowDecimals={false}
                  width={58}
                  tick={{ fontSize: 12, fill: '#556070' }}
                  tickLine={false}
                  axisLine={false}
                  label={{
                    value: 'REFERRALS',
                    angle: -90,
                    position: 'insideLeft',
                    offset: 8,
                    style: {
                      textAnchor: 'middle',
                      fill: '#6b7280',
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: 2,
                    },
                  }}
                />
                <Tooltip content={<MonthlyTooltip tooltipLabel={tooltipLabel} />} cursor={{ fill: 'rgba(35, 18, 79, 0.06)' }} />
                <Bar
                  dataKey="referrals"
                  isAnimationActive={false}
                  radius={0}
                  minPointSize={8}
                >
                  <LabelList
                    content={({ x, y, width, value }) => {
                      if (value === undefined || value === null) {
                        return null;
                      }

                      return (
                        <text
                          x={x + width / 2}
                          y={Math.max(y - 10, 14)}
                          textAnchor="middle"
                          fill="#1f2d4d"
                          fontSize={13}
                          fontWeight={700}
                        >
                          {value}
                        </text>
                      );
                    }}
                  />
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.month_key}
                      fill={entry.isCurrentMonth ? CURRENT_MONTH_COLOR : BASE_BAR_COLOR}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
